/* eslint-disable no-console */
import { find, isEmpty, includes, some, sortBy, map } from 'lodash'
import {
  AlertSeverityEnum,
  SuppressionTypeEnum,
  ObjectSubordinationEnum,
  coordinates,
  CommonTypeCount
} from '@src/endpoints/services/commonHdr.service'
import type {
  EventAlertChart,
  UserVolumeAlertChart,
  LocationAlertChart
} from '@src/endpoints/services/skynet.service'
import type { LocationData } from '@src/endpoints/services/usagemanager.service'
import {
  AlertTypeEnum,
  AlertsInfo,
  AlertTypeCount
} from '@src/endpoints/services/intentMgr.service'
import type { ResourceTypeDict } from '@src/redux/reducers/shell'
import type { AlertItem } from '@src/redux/reducers/alerts'
import type {
  Graph,
  EdgeColor,
  EdgeСustomStyle
} from '@src/components/vis.js.network.vis/models'

export {
  AlertSeverityEnum,
  SuppressionTypeEnum,
  ObjectSubordinationEnum,
  AlertTypeEnum
}

export interface AlertTab {
  title: string
  type: string
  badge: number
  isType?: boolean
  isEntity?: boolean
  order: number
}

export const AlertStatus = {
  new: 'NEW',
  open: 'OPEN',
  ignore: 'IGNORE',
  close: 'CLOSE'
}
export interface EventAlertsPoint {
  color?: string
  name?: string
  order?: number
  size?: number
  x?: number
  y?: number
}

export const parseChart = (chart: string) => {
  try {
    return JSON.parse(chart || 'null')
  } catch (e) {
    console.log('json parse failed')
  }
}

export function formatUserVolumeAlertChart(data: UserVolumeAlertChart = {}) {
  const {
    barchart_alert_point,
    x_axis_name,
    y_axis_name,
    color,
    action_names,
    alert_x,
    alert_y
  } = data
  return {
    points: barchart_alert_point || [],
    xAxisTitle: x_axis_name,
    yAxisTitle: y_axis_name,
    colors: color || [],
    actionNames: action_names || [],
    alertX: alert_x,
    alertY: alert_y
  }
}

export const isRedColor = (color: string) =>
  color === 'red' || color === 'rgb(255,0,0)'
export const isRedPoint = (point: EventAlertsPoint) => isRedColor(point.color)

export function formatEventAlertsChart(data: EventAlertChart = {}) {
  const { points = [], x_axis_name, y_axis_name } = data

  return {
    points: points.map(
      item =>
        ({
          name: item.point_name,
          color: item.point_color,
          order: isRedColor(item.point_color) ? 1 : 0,
          x: item.x,
          y: item.y,
          size: Number(item.point_size)
        } as EventAlertsPoint)
    ),
    xAxisTitle: x_axis_name,
    yAxisTitle: y_axis_name
  }
}

export const getLocationName = ({
  city,
  state,
  country
}: {
  city?: string
  state?: string
  country?: string
}) => [city, state, country].filter(x => x).join(', ')

export function formatSuspiciousLocationAlert(data: LocationAlertChart = {}) {
  const { alert_location = {}, previous_locations = [] } = data
  const locationMarkers: {
    name?: string
    coordinates?: [number, number]
    radius?: number
    color?: string
  }[] = []

  //add alert location
  if (alert_location) {
    const {
      coordinates = {},
      city,
      state,
      country,
      radius
    } = alert_location || ({} as LocationData)
    const { longitude, latitude } = coordinates || ({} as coordinates)
    if (longitude && latitude) {
      locationMarkers.push({
        name: getLocationName({ city, state, country }),
        coordinates: [longitude, latitude],
        radius,
        color: '#dd2c00'
      })
    }
  }
  //add Previous Locations
  for (let i = 0; i < previous_locations.length; i++) {
    const {
      coordinates = {},
      city,
      state,
      country,
      radius
    } = previous_locations[i] || ({} as LocationData)
    const { longitude, latitude } = coordinates || ({} as coordinates)
    if (longitude && latitude) {
      locationMarkers.push({
        name: getLocationName({ city, state, country }),
        coordinates: [longitude, latitude],
        radius,
        color: 'orange'
      })
    }
  }
  return locationMarkers
}

interface AlertDetailsResult extends AlertsInfo {
  graph?: Graph
  barChart?: ReturnType<typeof formatUserVolumeAlertChart>
  mapLocations?: ReturnType<typeof formatSuspiciousLocationAlert>
  eventAlertsChart?: ReturnType<typeof formatEventAlertsChart>
  hasQuery?: boolean
  userActivityWithFilter?: boolean
}

export function parseAlertDetails(alert: AlertsInfo): AlertDetailsResult {
  const {
    chart,
    alert_type,
    monitor_name,
    edge_id_list = [],
    alert_query
  } = alert || ({} as AlertsInfo)

  const isAlertAI = alert_type === AlertTypeEnum.ALERT_TYPE_AI
  const isBuiltInRiskModel = monitor_name === 'Built-in Risk Model'

  let graph: Graph = null

  const hasQuery = !isEmpty(alert_query)

  if (!!alert && alert.result_graph) {
    try {
      graph = JSON.parse(alert.result_graph) || {}
    } catch (e) {
      console.log('json parse failed')
    }
  }
  if (isAlertAI || isBuiltInRiskModel) {
    const edgesWithAlert = edge_id_list.map(x => x)
    const { links = [] } = graph || ({} as Graph)
    links.forEach((l, index) => {
      const flag = find(edgesWithAlert, id => id === l.mlink)
      if (flag) {
        links[index] = {
          ...l,
          customStyle: {
            width: 10,
            color: {
              enabled: true,
              color: '#f20360',
              highlight: '#f20360',
              inherit: false
            } as EdgeColor
          } as EdgeСustomStyle
        }
      }
    })
  }
  const parsedChart = parseChart(chart)

  const alertDetailsProps = {
    ...alert,
    graph,
    hasQuery,
    userActivityWithFilter:
      hasQuery &&
      includes(
        [
          AlertTypeEnum.ALERT_TYPE_AI,
          AlertTypeEnum.ALERT_TYPE_USAGE,
          AlertTypeEnum.ALERT_TYPE_LOCATION
        ],
        alert_type
      )
  }

  switch (alert_type) {
    case AlertTypeEnum.ALERT_TYPE_USAGE:
      return {
        ...alertDetailsProps,
        barChart: formatUserVolumeAlertChart(parsedChart)
      }
    case AlertTypeEnum.ALERT_TYPE_LOCATION:
      return {
        ...alertDetailsProps,
        mapLocations: formatSuspiciousLocationAlert(parsedChart)
      }
    case AlertTypeEnum.ALERT_TYPE_EVENT:
      return {
        ...alertDetailsProps,
        eventAlertsChart: formatEventAlertsChart(parsedChart)
      }
    default:
      return alertDetailsProps
  }
}

export function getAlertSeverityCounts(alertList: AlertItem[]) {
  let high = 0
  let medium = 0
  let low = 0
  alertList.forEach(item => {
    switch (item.severity) {
      case AlertSeverityEnum.ALERT_SEVERITY_HIGH:
        high++
        break
      case AlertSeverityEnum.ALERT_SEVERITY_MEDIUM:
        medium++
        break
      case AlertSeverityEnum.ALERT_SEVERITY_LOW:
        low++
        break
      default:
        break
    }
    return item
  })
  return { high, medium, low }
}

export function getAlertSeverity(
  alertList: AlertItem[],
  severity: AlertSeverityEnum
) {
  alertList = alertList || []
  const values = alertList.filter(item => {
    if (item.severity === severity) {
      return true
    }
    return false
  })
  const retArr = values || []
  return retArr
}

export function normalizeAlertList(alertsList: AlertItem[]) {
  const alerts = alertsList.map(item => {
    const status =
      item && item.status
        ? item.status.replace(new RegExp('^ALERT_STATUS_'), '')
        : ''
    return {
      ...item,
      status
    }
  })
  return alerts
}

export function normalizeInfoAlertList(infoAlertList: AlertItem[]) {
  // the following handles a corner case backend bug gracefully
  if (
    infoAlertList &&
    infoAlertList.length === 1 &&
    typeof infoAlertList[0].status === 'undefined'
  ) {
    return []
  }
  const alerts = infoAlertList.map(item => {
    const status =
      item && item.status
        ? item.status.replace(new RegExp('^ALERT_STATUS_'), '')
        : ''
    return {
      ...item,
      status
    }
  })
  return alerts
}

interface AlertType extends AlertTypeCount {
  tabType?: string
}
export function getAlertTabs(
  types: AlertType[] = [],
  resources: CommonTypeCount[] = [],
  resourceTypes: ResourceTypeDict = {}
) {
  let alertTabs: AlertTab[] = map(
    types,
    ({
      alert_type,
      high_count,
      tabType = (alert_type || '').replace('ALERT_TYPE_', '')
    }) => ({
      title: tabType,
      type: tabType,
      badge: high_count || 0,
      isType: true,
      order: 0
    })
  )
  resources.forEach(item => {
    if (!some(alertTabs, { type: item.type }))
      alertTabs.push({
        title: resourceTypes[item.type]?.display_name || item.type,
        type: item.type,
        badge: item.high_count || 0,
        isEntity: true,
        order:
          item.type === 'Suspicious Event' || item.type === 'Location' ? 1 : 2
      })
  })

  alertTabs = sortBy(
    alertTabs.filter(tab => tab.title !== 'UNKNOWN'),
    item => [item.title !== 'All', item.order, item.title]
  )

  return alertTabs
}

export const changeStatusColors = {
  OBJECT_STATUS_UNCHANGED: 'transparent',
  OBJECT_STATUS_ADDED: '#009c00',
  OBJECT_STATUS_DELETED: '#f20360'
}
